import { useCallback, useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "./reduxHooks";
import {
  selectDrawerSelectedAccountId,
  selectObjectStatusesByUniqueId,
  selectReportObjectStatusCatalog,
  selectReportObjectStatusCatalogAccountId,
  selectReportObjectStatusCatalogLoaded,
  selectReportsState
} from "app/data/selectors";
import {
  getReportObjectStatusCatalogThunk,
  setReportsFilterData
} from "app/features";
import {
  applyReportInactiveFilter,
  buildReportObjectStatusLookups
} from "app/data/helpers/reportInactiveObjects.helpers";

const useReportInactiveObjects = () => {
  const dispatch = useAppDispatch();
  const drawerSelectedAccountId = useAppSelector(selectDrawerSelectedAccountId);
  const catalog = useAppSelector(selectReportObjectStatusCatalog);
  const catalogAccountId = useAppSelector(
    selectReportObjectStatusCatalogAccountId
  );
  const catalogLoaded = useAppSelector(selectReportObjectStatusCatalogLoaded);
  const objectStatusesByUniqueId = useAppSelector(
    selectObjectStatusesByUniqueId
  );
  const { reportsFilterData } = useAppSelector(selectReportsState);

  const showInactiveObjects = Boolean(reportsFilterData?.showInactiveObjects);

  const setShowInactiveObjects = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      const next =
        typeof value === "function" ? value(showInactiveObjects) : value;
      dispatch(
        setReportsFilterData({
          ...reportsFilterData,
          showInactiveObjects: Boolean(next)
        })
      );
    },
    [dispatch, reportsFilterData, showInactiveObjects]
  );

  const statusLookupsReady =
    catalogLoaded && catalogAccountId === drawerSelectedAccountId;

  useEffect(() => {
    if (!drawerSelectedAccountId) return;
    if (statusLookupsReady) return;
    dispatch(getReportObjectStatusCatalogThunk(drawerSelectedAccountId));
  }, [drawerSelectedAccountId, dispatch, statusLookupsReady]);

  const lookups = useMemo(() => {
    const fromCatalog = buildReportObjectStatusLookups(
      (catalog || []).map((object) => ({
        uniqueId: object?.uniqueId,
        name: object?.name,
        aNumber: object?.aNumber,
        serialNumber: object?.serialNumber,
        status: object?.status
      }))
    );

    Object.entries(objectStatusesByUniqueId || {}).forEach(([id, status]) => {
      if (!Object.prototype.hasOwnProperty.call(fromCatalog.byUniqueId, id)) {
        fromCatalog.byUniqueId[id] = status;
      }
    });

    return fromCatalog;
  }, [catalog, objectStatusesByUniqueId]);

  const applyInactiveFilter = useCallback(
    (rows: any[] | null | undefined) =>
      applyReportInactiveFilter(
        rows,
        lookups,
        showInactiveObjects,
        statusLookupsReady
      ),
    [lookups, showInactiveObjects, statusLookupsReady]
  );

  return {
    showInactiveObjects,
    setShowInactiveObjects,
    lookups,
    statusLookupsReady,
    applyInactiveFilter
  };
};

export default useReportInactiveObjects;
